import React, { useEffect, useRef, useState } from "react";
import {
  DrawData,
  InspectorForm,
  Metrics,
  Photo,
  SovItem,
  TabId,
  Project,
} from "../types/inspector.types";
import { statusOptions, tabItems } from "../constants/inspectorData";
import useScrollToTop from "../../../../hooks/useScrollToTop";
import TopBar from "../components/TopBar";
import TabBar from "../components/TabBar";
import FiTab from "../components/FiTab";
import DrawTab from "../components/DrawTab";
import PhotosTab from "../components/PhotosTab";
import NotesTab from "../components/NotesTab";
import SubmitTab from "../components/SubmitTab";
import ProjectSwitcherModal from "../components/ProjectSwitcherModal";

interface CommercialInspectorPageProps {
  currentProject: Project;
  projects: Project[];
  currentProjectId: string;
  sovItems: SovItem[];
  notDrawnItems: [string, string, string][];
  photos: Photo[];
  generalNotes: string;
  flagNotes: string;
  offsiteNotes: string;
  inspectorForm: InspectorForm;
  drawData: DrawData;
  drawAmount: string;
  drawPercentage: string;
  metrics: Metrics;
  isSaving: boolean;
  isSubmitting: boolean;
  saveError: string;
  submitError: string;
  saveDraftSuccess: boolean;
  submitted: boolean;
  onSetStatus: (id: string, status: number) => void;
  onSetCustomPct: (id: string, rawValue: string) => void;
  onClearCustomPct: (id: string) => void;
  onToggleFlag: (id: string) => void;
  onFlagWithComment: (id: string, text: string) => void;
  onAddComment: (id: string, text: string) => void;
  onAddPhoto: (newPhotos: Photo[]) => void;
  onUpdateCaption: (id: string, caption: string) => void;
  onPhotoUploaded: (id: string, s3Key: string) => void;
  onPhotoUploadFailed: (id: string, error: string) => void;
  onRemovePhoto: (id: string) => void;
  onGeneralNotesChange: (notes: string) => void;
  onFlagNotesChange: (notes: string) => void;
  onOffsiteNotesChange: (notes: string) => void;
  onInspectorFormChange: (field: keyof InspectorForm, value: string) => void;
  onSubmit: () => void;
  onSaveDraft: () => void;
  onNewInspection: () => void;
  onProjectSelect: (id: string) => void;
  onOpenDirections: () => void;
}

export default function CommercialInspectorPage({
  currentProject,
  projects,
  currentProjectId,
  sovItems,
  notDrawnItems,
  photos,
  generalNotes,
  flagNotes,
  offsiteNotes,
  inspectorForm,
  drawData,
  drawAmount,
  drawPercentage,
  metrics,
  isSaving,
  isSubmitting,
  saveError,
  submitError,
  saveDraftSuccess,
  submitted,
  onSetStatus,
  onSetCustomPct,
  onClearCustomPct,
  onToggleFlag,
  onFlagWithComment,
  onAddComment,
  onAddPhoto,
  onUpdateCaption,
  onPhotoUploaded,
  onPhotoUploadFailed,
  onRemovePhoto,
  onGeneralNotesChange,
  onFlagNotesChange,
  onOffsiteNotesChange,
  onInspectorFormChange,
  onSubmit,
  onSaveDraft,
  onNewInspection,
  onProjectSelect,
  onOpenDirections,
}: CommercialInspectorPageProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<TabId>("fi");
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const activeTabIndex = tabItems.findIndex((t) => t.id === activeTab);
  useScrollToTop(activeTabIndex, {
    targetRef: scrollRef as React.RefObject<HTMLElement | null>,
  });
  useScrollToTop(submitted ? 1 : 0, {
    targetRef: scrollRef as React.RefObject<HTMLElement | null>,
  });

  useEffect(() => {
    if (submitted) setActiveTab("submit");
  }, [submitted]);

  const toggleSovSection = (id: string) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNewInspection = () => {
    setActiveTab("fi");
    onNewInspection();
  };

  return (
    <div className="inspectorPage">
      <TopBar
        project={currentProject}
        onDirections={onOpenDirections}
        onSwitch={() => setShowProjectModal(true)}
      />
      <div className="inspectorContent">
      <TabBar
        tabs={tabItems}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        locked={submitted}
      />

      <div className="scroll" ref={scrollRef}>
        <div className={`screen${activeTab === "fi" ? " active" : ""}`}>
          <FiTab
            project={currentProject}
            metrics={metrics}
            sovItems={sovItems}
            notDrawnItems={notDrawnItems}
            collapsed={collapsed}
            statusOptions={statusOptions}
            drawAmount={drawAmount}
            drawPercentage={drawPercentage}
            onToggleSov={toggleSovSection}
            onSetStatus={onSetStatus}
            onSetCustomPct={onSetCustomPct}
            onClearCustomPct={onClearCustomPct}
            onToggleFlag={onToggleFlag}
            onFlagWithComment={onFlagWithComment}
            onAddComment={onAddComment}
          />
        </div>

        <div className={`screen${activeTab === "draw" ? " active" : ""}`}>
          <DrawTab
            drawData={drawData}
            sovItems={sovItems}
            drawPercentage={drawPercentage}
            cscEstimation={inspectorForm.estimate}
            flagNotes={flagNotes}
          />
        </div>

        <div className={`screen${activeTab === "photos" ? " active" : ""}`}>
          <PhotosTab
            photos={photos}
            onAddPhoto={onAddPhoto}
            onUpdateCaption={onUpdateCaption}
            onPhotoUploaded={onPhotoUploaded}
            onPhotoUploadFailed={onPhotoUploadFailed}
            onRemovePhoto={onRemovePhoto}
          />
        </div>

        <div className={`screen${activeTab === "notes" ? " active" : ""}`}>
          <NotesTab
            generalNotes={generalNotes}
            flagNotes={flagNotes}
            offsiteNotes={offsiteNotes}
            sovItems={sovItems}
            onGeneralNotesChange={onGeneralNotesChange}
            onFlagNotesChange={onFlagNotesChange}
            onOffsiteNotesChange={onOffsiteNotesChange}
          />
        </div>

        <div className={`screen${activeTab === "submit" ? " active" : ""}`}>
          <SubmitTab
            metrics={metrics}
            inspectorForm={inspectorForm}
            submitted={submitted}
            projectName={currentProject.name}
            isSaving={isSaving}
            isSubmitting={isSubmitting}
            saveError={saveError}
            submitError={submitError}
            saveDraftSuccess={saveDraftSuccess}
            onInspectorFormChange={onInspectorFormChange}
            onSubmit={onSubmit}
            onSaveDraft={onSaveDraft}
            onNewInspection={handleNewInspection}
          />
        </div>
      </div>
      </div>

      <ProjectSwitcherModal
        projects={projects}
        currentProjectId={currentProjectId}
        open={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        onSelect={(id) => {
          onProjectSelect(id);
          setShowProjectModal(false);
        }}
      />
    </div>
  );
}
